import type { ReactNode } from 'react';
import ModuleLayout, { type BreadcrumbItem, type ModuleMetric } from '@/components/admin/ModuleLayout';
import { EntityDetailNavLayout, type EntityDetailNavGroup } from '@/components/admin/EntityDetailNav';

export type RegionalTabKey = 'info' | 'equipos' | 'configuracion' | 'podio' | 'puntaje' | 'clasificacion' | 'lanzamiento';

const REGIONAL_TAB_LABELS: Record<RegionalTabKey, string> = {
  info: 'Información',
  equipos: 'Equipos',
  configuracion: 'Configuración',
  podio: 'Podio',
  puntaje: 'Puntaje',
  clasificacion: 'Clasificación',
  lanzamiento: 'Control de lanzamiento',
};

interface RegionalShellProps {
  mode: 'create' | 'edit';
  activeTab: RegionalTabKey;
  onTabChange: (tab: RegionalTabKey) => void;
  breadcrumbs: BreadcrumbItem[];
  title: string;
  subtitle: string;
  metrics?: ModuleMetric[];
  headerActions?: ReactNode;
  tabLabels?: Partial<Record<RegionalTabKey, string>>;
  children: ReactNode;
}

// Shared shell for Regional create/view/edit (cccd-ux-redesign —
// cccd-admin-tabbed-shell): mirrors EventShell's tab-bar pattern exactly,
// giving Regional the same Información/Equipos/Configuración/Podio parity
// with National that the redesign requires.
export function RegionalShell({ mode, activeTab, onTabChange, breadcrumbs, title, subtitle, metrics, headerActions, tabLabels, children }: RegionalShellProps) {
  const locked = (key: RegionalTabKey) => mode === 'create' && key !== 'info';
  const label = (key: RegionalTabKey) => tabLabels?.[key] ?? REGIONAL_TAB_LABELS[key];
  const groups: EntityDetailNavGroup<RegionalTabKey>[] = [
    { label: 'GENERAL', items: [
      { key: 'info', label: label('info'), locked: locked('info') },
      { key: 'configuracion', label: label('configuracion'), locked: locked('configuracion') },
    ] },
    { label: 'COMPETENCIA', items: [
      { key: 'equipos', label: label('equipos'), locked: locked('equipos') },
      { key: 'puntaje', label: label('puntaje'), locked: locked('puntaje') },
      { key: 'clasificacion', label: label('clasificacion'), locked: locked('clasificacion') },
      { key: 'lanzamiento', label: label('lanzamiento'), locked: locked('lanzamiento') },
      { key: 'podio', label: label('podio'), locked: locked('podio') },
    ] },
  ];

  return (
    <ModuleLayout
      breadcrumbs={breadcrumbs}
      title={title}
      subtitle={subtitle}
      tabs={[
        { label: 'Panel', to: '/cccd' },
        { label: 'Nacionales', to: '/cccd/events' },
        { label: 'Regionales', to: '/cccd/regionals', active: true },
      ]}
      metrics={metrics ?? []}
      actions={headerActions}
    >
      <EntityDetailNavLayout groups={groups} activeKey={activeTab} onChange={(key) => !locked(key) && onTabChange(key)}>
        {children}
      </EntityDetailNavLayout>
    </ModuleLayout>
  );
}
